import React, { useState } from 'react';

interface ContentWarningProps {
  reason?: string;
  children: React.ReactNode;
  onReveal?: () => void;
}

/**
 * ContentWarning - Handles NIP-36 content warnings
 * Blurs content by default, allows user to reveal it
 */
export function ContentWarning({ reason, children, onReveal }: ContentWarningProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [policy] = useState(() => localStorage.getItem('content-warning-policy') || 'blur');

  if (policy === 'show') {
    return <>{children}</>;
  }

  if (policy === 'hide') {
    return null;
  }

  // Default: blur
  if (isRevealed) {
    return <>{children}</>;
  }

  return (
    <div className="relative">
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm rounded-2xl p-4 text-center select-none">
        <div className="flex items-center gap-1.5 text-xs text-amber-500 font-semibold mb-1.5">
          <span>⚠️ Content Warning</span>
        </div>
        {reason && (
          <p className="text-[11px] text-muted-foreground mb-2.5 leading-normal max-w-[200px]">
            {reason}
          </p>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsRevealed(true);
            onReveal?.();
          }}
          className="px-4 py-1.5 rounded-full bg-foreground/10 hover:bg-foreground/20 text-xs font-bold text-foreground transition-colors cursor-pointer"
        >
          Show content
        </button>
      </div>
      <div className="opacity-20 blur-sm pointer-events-none select-none overflow-hidden max-h-[200px]">
        {children}
      </div>
    </div>
  );
}